import { ECexName } from 'web3';

/**
 * Generate tick size options for orderbook aggregation.
 * Uses a fixed list of trader-friendly "nice" values - NO ugly numbers like 2, 20, 200, 2000!
 *
 * Tick size is the price increment for grouping orders, not decimal precision!
 * For example, with BTC at $90,000:
 * - tickSize=10 groups: 90000, 90010, 90020 (useful!)
 * - NOT precision=2 which would show: 90000.00, 90000.01, 90000.02 (useless!)
 */

/**
 * Full list of "nice" tick sizes including x5 intermediates.
 * Used for lower-priced assets where we don't need large aggregation.
 * Pattern: 0.01 → 0.05 → 0.1 → 0.5 → 1 → 5 → 10 → 50
 */
const NICE_TICKS_WITH_HALVES: readonly number[] = [0.01, 0.05, 0.1, 0.5, 1, 5, 10, 50] as const;

/**
 * Compact list of "nice" tick sizes - powers of 10 only.
 * Used for high-priced assets (BTC, ETH) to fit larger values like 100.
 * Pattern: 0.01 → 0.1 → 1 → 10 → 100
 */
const NICE_TICKS_POWERS_OF_10: readonly number[] = [0.01, 0.1, 1, 10, 100] as const;

/**
 * Check if tick size is "very small" (multiple zeros before significant digit).
 * For these, we just use powers of 10.
 */
const isVerySmallTickSize = (tick: number): boolean => tick < 0.01;

/**
 * Round to avoid floating point errors.
 */
const roundTick = (value: number): number => {
  if (value >= 1) return Math.round(value * 100) / 100;
  const decimals = Math.ceil(-Math.log10(value)) + 2;
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
};

/**
 * Determine the optimal number of tick size options based on price.
 * Lower prices = fewer options (simpler UI), higher prices = more options (more control).
 *
 * @param currentPrice - Current market price
 * @returns Target number of options (3-4)
 */
const getTargetOptionsCount = (currentPrice: number): number => {
  if (!Number.isFinite(currentPrice) || currentPrice <= 0) {
    return 3; // Default to minimal options when price unknown
  }

  // Very low price tokens (memecoins < $0.01): 3 options
  if (currentPrice < 0.01) {
    return 3;
  }

  // Low price tokens ($0.01 - $1): 3 options
  if (currentPrice < 1) {
    return 3;
  }

  // Mid price tokens ($1 - $100): 4 options
  if (currentPrice < 100) {
    return 4;
  }

  // High price tokens ($100+): 4 options
  return 4;
};

/**
 * Determine maximum allowed tick size based on price.
 * Prevents generating tick sizes that would be too large relative to price.
 *
 * @param currentPrice - Current market price
 * @returns Maximum tick size allowed
 */
const getMaxAllowedTick = (currentPrice: number): number => {
  if (!Number.isFinite(currentPrice) || currentPrice <= 0) {
    return 0.001; // Conservative fallback
  }

  // Max tick should be ~1-10% of price for meaningful aggregation
  // For very low prices, we need much smaller ticks
  if (currentPrice < 0.0001) {
    return currentPrice * 100; // Up to 100x the price for micro-tokens
  }
  if (currentPrice < 0.001) {
    return currentPrice * 50;
  }
  if (currentPrice < 0.01) {
    return currentPrice * 20;
  }
  if (currentPrice < 0.1) {
    return currentPrice * 10;
  }
  if (currentPrice < 1) {
    return 0.1;
  }
  if (currentPrice < 10) {
    return 1;
  }
  if (currentPrice < 100) {
    return 10;
  }
  if (currentPrice < 1000) {
    return 50;
  }

  // High price tokens (BTC, ETH)
  return 100;
};

/**
 * Generate tick size options based on base tick size and current price.
 * Uses a fixed list of "nice" trader-friendly values.
 *
 * The number of options adapts to price:
 * - Very low price (< $0.01): 3 options
 * - Low price ($0.01 - $1): 3 options
 * - Mid price ($1 - $100): 4 options
 * - High price ($100+): 4 options
 *
 * @param baseTickSize - Minimum tick size from exchange API
 * @param currentPrice - Current market price for the pair
 * @returns Array of 3-4 tick size options sorted ascending
 *
 * @example
 * generateSmartTickSizeOptions(0.000001, 0.000005) // Micro-memecoin
 * // Returns: [0.000001, 0.00001, 0.0001] (3 options)
 *
 * @example
 * generateSmartTickSizeOptions(0.0001, 0.5) // Sub-dollar token
 * // Returns: [0.0001, 0.001, 0.01] (3 options)
 *
 * @example
 * generateSmartTickSizeOptions(0.01, 50) // Mid-price token
 * // Returns: [0.01, 0.1, 1, 10] (4 options)
 *
 * @example
 * generateSmartTickSizeOptions(0.01, 90000) // BTC
 * // Returns: [0.01, 0.1, 1, 10] (4 options)
 */
export function generateSmartTickSizeOptions(baseTickSize: number, currentPrice: number): number[] {
  if (!Number.isFinite(baseTickSize) || baseTickSize <= 0) {
    return [];
  }

  const targetCount = getTargetOptionsCount(currentPrice);
  const maxAllowedTick = getMaxAllowedTick(currentPrice);

  // For very small tick sizes (< 0.01), generate powers of 10
  if (isVerySmallTickSize(baseTickSize)) {
    const options: number[] = [baseTickSize];
    let current = baseTickSize;

    // Generate powers of 10 until we reach target count or max allowed
    while (options.length < targetCount) {
      current = roundTick(current * 10);

      // Stop if we exceed max allowed tick
      if (current > maxAllowedTick) break;

      options.push(current);
    }

    return options;
  }

  // For normal tick sizes (>= 0.01), use the predefined nice values
  // Use powers of 10 for cleaner progression
  const niceTicksList = NICE_TICKS_POWERS_OF_10;

  // Find starting index (first nice value >= baseTickSize)
  const startIndex = niceTicksList.findIndex(t => t >= baseTickSize);
  if (startIndex === -1) {
    // baseTickSize is larger than all nice values, return just it
    return [baseTickSize];
  }

  // Build options list
  const options: number[] = [];

  // If baseTickSize is not exactly in the nice list, include it first
  const baseIsInList = niceTicksList.some(t => Math.abs(t - baseTickSize) < 0.0000001);
  if (!baseIsInList && baseTickSize < niceTicksList[startIndex]) {
    options.push(baseTickSize);
  }

  // Add nice values starting from startIndex
  for (let i = startIndex; i < niceTicksList.length; i++) {
    if (options.length >= targetCount) break;

    const tick = niceTicksList[i];

    // Stop if we exceed max allowed for this price
    if (tick > maxAllowedTick) break;

    options.push(tick);
  }

  return options;
}

/**
 * Legacy function for backward compatibility.
 * @deprecated Use generateSmartTickSizeOptions instead for price-aware generation.
 */
export function generateTickSizeOptions(
  baseTickSize: number,
  maxValue = 100,
  maxOptions = 5,
): number[] {
  if (!Number.isFinite(baseTickSize) || baseTickSize <= 0) {
    return [];
  }

  // For very small tick sizes, use powers of 10
  if (isVerySmallTickSize(baseTickSize)) {
    const options: number[] = [baseTickSize];
    let current = baseTickSize;
    while (options.length < maxOptions && current < maxValue) {
      current = roundTick(current * 10);
      if (current <= maxValue) {
        options.push(current);
      }
    }
    return options;
  }

  // For normal tick sizes, filter nice values (use full list for legacy function)
  const options = NICE_TICKS_WITH_HALVES.filter(t => t >= baseTickSize && t <= maxValue);

  if (options.length <= maxOptions) {
    return [...options];
  }

  // Take first maxOptions
  return options.slice(0, maxOptions);
}

/**
 * Format tick size for display in dropdown.
 * Handles both small decimals (avoiding scientific notation) and large whole numbers.
 *
 * @param tickSize - Tick size value to format
 * @returns Formatted string for display (never in scientific notation)
 *
 * @example
 * formatTickSizeForDisplay(0.01) → "0.01"
 * formatTickSizeForDisplay(0.1) → "0.1"
 * formatTickSizeForDisplay(1) → "1"
 * formatTickSizeForDisplay(10) → "10"
 * formatTickSizeForDisplay(1000) → "1000"
 * formatTickSizeForDisplay(0.00000001) → "0.00000001" (NOT "1e-8")
 */
export function formatTickSizeForDisplay(tickSize: number): string {
  // For values >= 1, show as integer
  if (tickSize >= 1) {
    return tickSize.toString();
  }

  // For very small decimals, use toFixed with appropriate precision
  // to avoid scientific notation like "1e-8"
  if (tickSize < 0.0001) {
    const decimalPlaces = Math.max(0, -Math.floor(Math.log10(tickSize)));
    return tickSize.toFixed(decimalPlaces);
  }

  // For regular decimals (0.0001 to 0.9999), toString and remove trailing zeros
  return tickSize.toString().replace(/\.?0+$/, '');
}

/**
 * Get suggested tick size based on current price.
 * Helps select a reasonable default tick size.
 *
 * @param baseTickSize - Minimum tick size from exchange
 * @param currentPrice - Current market price
 * @returns Suggested tick size (typically 0.1% to 1% of price)
 *
 * @example
 * // BTC at $50,000 with baseTickSize=0.01
 * getSuggestedTickSize(0.01, 50000)
 * // Returns: 50 (0.1% of price, reasonable grouping)
 */
export function getSuggestedTickSize(baseTickSize: number, currentPrice: number): number {
  const options = generateSmartTickSizeOptions(baseTickSize, currentPrice);

  if (options.length === 0) {
    return baseTickSize;
  }

  // Find tick size that's approximately 0.1% of current price
  const target = currentPrice * 0.001; // 0.1%

  // Find closest option to target
  let closest = options[0];
  let minDiff = Math.abs(options[0] - target);

  for (const opt of options) {
    const diff = Math.abs(opt - target);
    if (diff < minDiff) {
      minDiff = diff;
      closest = opt;
    }
  }

  return closest;
}

/**
 * WhiteBIT multipliers for tick size generation.
 * Pattern: powers of 10 only (1, 10, 100, 1000...).
 * @see https://docs.whitebit.com/public/http-v4/#market-info
 */
const WHITEBIT_MULTIPLIERS = [1, 10, 100, 1000, 10000] as const;

/**
 * Maximum allowed tick size relative to price for WhiteBIT.
 * Prevents generating useless tick sizes that are too large for the current price.
 * WhiteBIT UI allows tick sizes up to ~50% of price for low-priced assets.
 *
 * @param currentPrice - Current market price
 * @returns Maximum tick size allowed
 */
const getWhitebitMaxAllowedTick = (currentPrice: number): number => {
  if (!Number.isFinite(currentPrice) || currentPrice <= 0) {
    return 100; // Conservative fallback
  }

  // Allow tick sizes up to ~50% of price
  return currentPrice * 0.5;
};

/**
 * Generate tick sizes following WhiteBIT's specific pattern.
 *
 * WhiteBIT derives tick sizes from the `moneyPrec` field in market metadata:
 * 1. Base tick = 10^(-moneyPrec) (e.g., moneyPrec=2 → baseTick=0.01)
 * 2. Multiples: baseTick × [1, 10, 100, 1000, ...]
 * 3. Capped based on current price to avoid useless large aggregations
 *
 * @param baseTickSize - Minimum tick size from WhiteBIT API (10^-moneyPrec)
 * @param currentPrice - Current market price for the pair
 * @returns Array of tick size options sorted ascending
 *
 * @see https://docs.whitebit.com/public/http-v4/#market-info
 *
 * @example
 * // ETH/USDC with moneyPrec=2 (baseTick=0.01), price ~3200
 * generateWhitebitTickSizeOptions(0.01, 3200)
 * // Returns: [0.01, 0.1, 1, 10, 100]
 *
 * @example
 * // Low-price token with moneyPrec=6 (baseTick=0.000001), price ~0.005
 * generateWhitebitTickSizeOptions(0.000001, 0.005)
 * // Returns: [0.000001, 0.00001, 0.0001] (capped by price)
 */
export function generateWhitebitTickSizeOptions(
  baseTickSize: number,
  currentPrice: number,
): number[] {
  if (!Number.isFinite(baseTickSize) || baseTickSize <= 0) {
    return [];
  }

  const maxAllowedTick = getWhitebitMaxAllowedTick(currentPrice);
  const options: number[] = [];

  for (const multiplier of WHITEBIT_MULTIPLIERS) {
    const tickSize = roundTick(baseTickSize * multiplier);

    // Stop if we exceed max allowed tick
    if (tickSize > maxAllowedTick) break;

    options.push(tickSize);
  }

  // Always include at least the base tick size
  if (options.length === 0) {
    return [baseTickSize];
  }

  return options;
}

/**
 * Factory function that delegates to exchange-specific tick size generators.
 *
 * This allows each exchange to have its own tick size generation logic that
 * matches their native UI behavior. Currently supports:
 * - WhiteBIT: Uses multiplier pattern from moneyPrec
 * - Other exchanges: Uses generic smart algorithm
 *
 * @param params - Configuration object
 * @param params.exchange - Exchange identifier (ECexName)
 * @param params.baseTickSize - Minimum tick size from exchange API
 * @param params.currentPrice - Current market price for the pair
 * @returns Array of tick size options sorted ascending
 *
 * @example
 * // WhiteBIT ETH/USDC
 * generateTickSizeOptionsForExchange({
 *   exchange: ECexName.whitebit,
 *   baseTickSize: 0.01,
 *   currentPrice: 3200
 * })
 * // Returns WhiteBIT-specific pattern: [0.01, 0.1, 1, 10, 100]
 *
 * @example
 * // Binance BTC/USDT (uses generic algorithm)
 * generateTickSizeOptionsForExchange({
 *   exchange: ECexName.binance,
 *   baseTickSize: 0.01,
 *   currentPrice: 90000
 * })
 * // Returns generic pattern: [0.01, 0.1, 1, 10, 100]
 */
export function generateTickSizeOptionsForExchange(params: {
  exchange?: ECexName;
  baseTickSize: number;
  currentPrice: number;
}): number[] {
  const { exchange, baseTickSize, currentPrice } = params;

  // Use exchange-specific generator when available
  if (exchange === ECexName.whitebit) {
    return generateWhitebitTickSizeOptions(baseTickSize, currentPrice);
  }

  // Coinstore uses similar power-of-10 aggregation as WhiteBIT
  if (exchange === ECexName.coinstore) {
    return generateWhitebitTickSizeOptions(baseTickSize, currentPrice);
  }

  // Default to generic smart algorithm for all other exchanges
  return generateSmartTickSizeOptions(baseTickSize, currentPrice);
}
